import * as React from 'react'

import { cn } from '@/lib/utils'

export interface HubSpotFormEmbedProps {
  portalId: string
  formId: string
  /** HubSpot data-center region, e.g. "na2" — determines the embed script's host. */
  region: string
  /** Fires once when a visitor successfully submits the embedded form. */
  onSubmitted?: () => void
  /** Shown until HubSpot reports the form ready, so the slot isn't blank while the embed loads. */
  fallback?: React.ReactNode
  /** Replaces `fallback` if the form still hasn't rendered after FORM_LOAD_TIMEOUT_MS (e.g. the script is blocked). */
  failedFallback?: React.ReactNode
  className?: string
}

export const FORM_LOAD_TIMEOUT_MS = 15_000

type FormLoadState = 'loading' | 'rendered' | 'failed'

/**
 * Embeds a HubSpot form via its v2 "hs-form-frame" snippet (script +
 * data-attributed div). Confirmed by testing: this form renders inside a
 * cross-origin iframe (`ui-forms-embed-components-app/frame.html`)
 * regardless of whether it's embedded this way or via the classic
 * `hbspt.forms.create()` API — both hit the same iframe rendering on
 * HubSpot's side for this portal/form. So this page's CSS cannot reach
 * the field/label/button styling inside it either way; restyle the form
 * itself from the HubSpot form editor's Style tab (Marketing → Forms →
 * this form), or check the form's Options for a non-iframe embed choice.
 */
export function HubSpotFormEmbed({
  portalId,
  formId,
  region,
  onSubmitted,
  fallback,
  failedFallback,
  className,
}: HubSpotFormEmbedProps) {
  const [loadState, setLoadState] = React.useState<FormLoadState>('loading')
  const hasFallback = fallback !== undefined

  React.useEffect(() => {
    const src = `https://js-${region}.hsforms.net/forms/embed/${portalId}.js`
    if (!document.querySelector(`script[src="${src}"]`)) {
      const script = document.createElement('script')
      script.src = src
      script.defer = true
      document.body.appendChild(script)
    }
  }, [portalId, region])

  React.useEffect(() => {
    if (!onSubmitted) return
    // HubSpot forms built in the updated forms editor (this one) report success
    // via the newer `hs-form-event:on-submission:success` window event, not the
    // legacy postMessage `hsFormCallback` contract — the two aren't guaranteed
    // to both fire, so both are handled here, guarded so a submission that
    // happens to trigger both only counts once.
    let fired = false
    const notifyOnce = () => {
      if (fired) return
      fired = true
      onSubmitted()
    }

    const expectedHost = `js-${region}.hsforms.net`
    function handleMessage(event: MessageEvent) {
      // Only trust messages actually sent by the HubSpot form host — otherwise
      // any other frame/script on the page could spoof a submission event and
      // fire a bogus conversion. Compare host only (not full origin/protocol)
      // since HubSpot mirrors the page's protocol, which is http in local dev.
      let originHost: string
      try {
        originHost = new URL(event.origin).host
      } catch {
        return
      }
      if (originHost !== expectedHost) return
      if (event.data?.type !== 'hsFormCallback' || event.data?.eventName !== 'onFormSubmitted') return
      if (event.data?.id !== formId) return
      notifyOnce()
    }

    function handleSubmissionEvent(event: Event) {
      const detail = (event as CustomEvent<{ formId?: string } | undefined>).detail
      if (detail?.formId !== formId) return
      notifyOnce()
    }

    window.addEventListener('message', handleMessage)
    window.addEventListener('hs-form-event:on-submission:success', handleSubmissionEvent)
    return () => {
      window.removeEventListener('message', handleMessage)
      window.removeEventListener('hs-form-event:on-submission:success', handleSubmissionEvent)
    }
  }, [formId, onSubmitted, region])

  React.useEffect(() => {
    if (!hasFallback) return
    // An iframe can appear before its form loads, so wait for HubSpot's own readiness event.
    const timeout = setTimeout(() => setLoadState('failed'), FORM_LOAD_TIMEOUT_MS)
    function handleReady(event: Event) {
      const detail = (event as CustomEvent<{ formId?: string } | undefined>).detail
      if (detail?.formId !== formId) return
      clearTimeout(timeout)
      setLoadState('rendered')
    }
    window.addEventListener('hs-form-event:on-ready', handleReady)
    return () => {
      clearTimeout(timeout)
      window.removeEventListener('hs-form-event:on-ready', handleReady)
    }
  }, [formId, hasFallback])

  const formHidden = hasFallback && loadState !== 'rendered'

  return (
    <div className="relative">
      {hasFallback && loadState === 'loading' && fallback}
      {hasFallback && loadState === 'failed' && (failedFallback ?? fallback)}
      <div
        className={cn(
          'hs-form-frame',
          formHidden && 'pointer-events-none absolute inset-x-0 top-0 opacity-0',
          className
        )}
        aria-hidden={formHidden || undefined}
        data-region={region}
        data-form-id={formId}
        data-portal-id={portalId}
      />
    </div>
  )
}
